/**
 * Column Type Validators
 *
 * A registry of built-in validators keyed by ColumnType. Each validator is a
 * pure function that takes a cell value string and a NormalizedColumnDefinition,
 * returning true if the value is valid for that type.
 *
 * These validators handle the built-in type checking only. Custom regex
 * validation and options list validation are handled separately in the
 * cell validation pipeline.
 */

import type { ColumnType, CellValidator } from '../types/template';

// ============================================================
// Regex patterns for built-in type validation
// ============================================================

/** Matches valid numeric strings: integer, decimal, optionally negative */
const NUMBER_REGEX = /^-?\d+(\.\d+)?$/;

/** Matches standard email format: local-part@domain.tld */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Matches ISO 8601 date format: YYYY-MM-DD (allows 1 or 2 digit M/D) */
const ISO_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}$/;

/** Accepted boolean string representations (checked case-insensitive) */
const BOOLEAN_VALUES = new Set(['true', 'false', 'yes', 'no', '1', '0']);

/** Matches phone-like strings: digits, spaces, hyphens, parens, optional leading +, must contain at least one digit */
const PHONE_REGEX = /^\+?[\d\s\-()]+$/;

// ============================================================
// Date format patterns
// ============================================================

/**
 * Common date format patterns mapped to regex.
 * These validate format shape only (correct separators and digit counts),
 * not semantic validity (e.g., month 01–12, day 01–31).
 * Allows 1 or 2 digits for month/day (e.g., 1/1/2025 or 01/01/2025).
 */
const DATE_FORMAT_PATTERNS: Record<string, RegExp> = {
  'YYYY-MM-DD': /^\d{4}-\d{1,2}-\d{1,2}$/,
  'MM/DD/YYYY': /^\d{1,2}\/\d{1,2}\/\d{4}$/,
  'DD/MM/YYYY': /^\d{1,2}\/\d{1,2}\/\d{4}$/,
  'MM-DD-YYYY': /^\d{1,2}-\d{1,2}-\d{4}$/,
  'DD-MM-YYYY': /^\d{1,2}-\d{1,2}-\d{4}$/,
};

// ============================================================
// Individual validators
// ============================================================

/**
 * String validator.
 * Required columns reject empty strings. Non-required columns accept anything.
 */
const validateString: CellValidator = (value, column) => {
  if (column.required) {
    return value.trim().length > 0;
  }
  return true;
};

/**
 * Number validator.
 * Accepts valid numeric strings: integer, decimal, optionally negative.
 */
const validateNumber: CellValidator = (value, _column) => {
  if (value === '') return false;
  return NUMBER_REGEX.test(value);
};

/**
 * Email validator.
 * Accepts local-part@domain format with at least one dot in the domain.
 */
const validateEmail: CellValidator = (value, _column) => {
  if (value === '') return false;
  return EMAIL_REGEX.test(value);
};

/**
 * Date validator.
 * Accepts ISO 8601 by default, or a specific format if dateFormat is provided.
 */
const validateDate: CellValidator = (value, column) => {
  if (value === '') return false;

  if (column.dateFormat) {
    const pattern = DATE_FORMAT_PATTERNS[column.dateFormat];
    if (pattern) {
      return pattern.test(value);
    }
    // Unknown dateFormat — fall back to ISO 8601
    return ISO_DATE_REGEX.test(value);
  }

  return ISO_DATE_REGEX.test(value);
};

/**
 * Boolean validator.
 * Accepts: "true", "false", "yes", "no", "1", "0" (case-insensitive).
 */
const validateBoolean: CellValidator = (value, _column) => {
  if (value === '') return false;
  return BOOLEAN_VALUES.has(value.toLowerCase());
};

/**
 * Phone validator (lenient).
 * Checks that the value contains only phone-like characters: digits, spaces,
 * hyphens, parentheses, and an optional leading +. Must contain at least one digit.
 * Does not enforce a minimum digit count — host apps needing strict phone
 * validation should use a custom regex on top.
 */
const validatePhone: CellValidator = (value, _column) => {
  if (value === '') return false;
  if (!PHONE_REGEX.test(value)) return false;

  // Must contain at least one digit to be a phone number
  return /\d/.test(value);
};

/**
 * Custom validator.
 * Accepts any value — custom validation is handled via regex in the cell
 * validation pipeline, not here.
 */
const validateCustom: CellValidator = (_value, _column) => {
  return true;
};

// ============================================================
// Validator registry
// ============================================================

/**
 * Registry of built-in validators keyed by ColumnType.
 * Used by the cell validation pipeline to look up the appropriate
 * validator for each column.
 */
export const columnTypeValidators: Record<ColumnType, CellValidator> = {
  string: validateString,
  number: validateNumber,
  email: validateEmail,
  date: validateDate,
  boolean: validateBoolean,
  phone: validatePhone,
  custom: validateCustom,
};
